import { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CheckIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ModalStepper } from '@/components/ui/Modal';
import {
  FIELD_HINT,
  FORM_SKIN,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CALLOUT,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
  SECTION_NUM,
  DASHED_RULE_STYLE,
} from '@/components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { LocationPicker } from '@/components/maps/LocationPicker';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import {
  useProvinces,
  useDistricts,
  useTownships,
} from '@/features/customers/hooks/useGeolocation';
import {
  getFriendlyErrorMessage,
  useGeolocationByCoordinates,
  useCreateAddress,
  useCreateBranchOffice,
} from '../hooks/useBranchOffice';
import { useUpdateBranchOffice } from '@/features/branchOffice/hooks/useBranchOffices';
import { branchOfficeService } from '../services/branchOfficeService';
import type { GeolocationData, BranchOffice as BranchOfficeAPI } from '../types/branchOffice.types';
import type { BranchOffice } from '@/features/branchOffice/types/branchOffice.types';
import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import Swal from 'sweetalert2';

/**
 * Convierte un error de axios/mutation en un mensaje amigable para el usuario,
 * evitando exponer textos crudos como "Request failed with status code 409".
 */

interface BranchOfficeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBranchOfficeCreated?: (branchOffice: BranchOfficeAPI) => void;
  branchOffice?: BranchOffice | null;
}

export function BranchOfficeFormModal({
  isOpen,
  onClose,
  onBranchOfficeCreated,
  branchOffice,
}: BranchOfficeFormModalProps) {
  const isEditMode = !!branchOffice;
  const [step, setStep] = useState(isEditMode ? 2 : 1);

  // Form data
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [geolocationData, setGeolocationData] = useState<GeolocationData | null>(null);
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [selectedTownshipId, setSelectedTownshipId] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [locationCode, setLocationCode] = useState(''); // Código de ubicación fiscal
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isActive, setIsActive] = useState(true);
  // Address ID for updating existing addresses
  const [currentAddressId, setCurrentAddressId] = useState<string | null>(null);

  // Hooks
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts(selectedProvinceId);
  const { data: townships, isLoading: isLoadingTownships } = useTownships(selectedDistrictId);
  // Plan validation
  const { validateCanAddBranchOffice } = useConsumption();

  const geolocationMutation = useGeolocationByCoordinates();
  const createAddressMutation = useCreateAddress();
  const createBranchOfficeMutation = useCreateBranchOffice();
  const updateBranchOfficeMutation = useUpdateBranchOffice();

  // State to track pending IDs while waiting for dropdown data to load
  const [pendingDistrictId, setPendingDistrictId] = useState<string | null>(null);
  const [pendingTownshipId, setPendingTownshipId] = useState<string | null>(null);

  // Populate form when editing - fetch full details from Aludra API
  useEffect(() => {
    const loadBranchOfficeDetails = async () => {
      if (isEditMode && branchOffice && selectedCompany?.id) {
        try {
          // Fetch complete branch office details from Aludra API
          const details = await branchOfficeService.getBranchOfficeDetails(
            selectedCompany.id,
            branchOffice.id
          );

          console.log('📋 Full branch office details loaded:', details);

          // Set basic fields
          setCode(details.Code || branchOffice.code);
          setDescription(details.Description || branchOffice.name);
          setEmail(details.EmailBranchOffice || branchOffice.email || '');
          setIsActive(details.IsActive ?? branchOffice.isActive);
          setAddressDetail(details.Address_AddressDetail || branchOffice.address || '');
          setLocationCode(details.Address_FiscalCode || branchOffice.fiscalCode || '');

          // Save the AddressId for updating
          if (details.AddressId) {
            setCurrentAddressId(details.AddressId);
            console.log('📍 Saved AddressId for updates:', details.AddressId);
          }

          // In edit mode, don't load coordinates - they may not match the address
          // User can optionally select new coordinates via the map if needed
          // Coordinates will only be updated if user explicitly selects a new location

          // Set geolocation data with IDs from Aludra
          setGeolocationData({
            Country: details.Address_CountryName || branchOffice.country || 'Panamá',
            CountryId: details.Address_CountryId || '',
            Province: details.Address_ProvinceName || branchOffice.province || '',
            ProvinceId: details.Address_ProvinceId || '',
            District: details.Address_DistrictName || branchOffice.district || '',
            DistrictId: details.Address_DistrictId || '',
            Township: details.Address_TownshipName || branchOffice.township || '',
            TownshipId: details.Address_TownshipId || '',
          } as GeolocationData);

          // Set province first, then save district and township IDs to set after data loads
          console.log('🏢 Setting location IDs from Aludra:', {
            provinceId: details.Address_ProvinceId,
            districtId: details.Address_DistrictId,
            townshipId: details.Address_TownshipId,
          });

          if (details.Address_ProvinceId) {
            // Normalize to lowercase since dropdown IDs are lowercase
            const provinceId = details.Address_ProvinceId.toLowerCase();
            setSelectedProvinceId(provinceId);
            console.log('✅ Setting province ID (normalized):', provinceId);

            // Save district and township IDs to set once their data loads
            if (details.Address_DistrictId) {
              setPendingDistrictId(details.Address_DistrictId.toLowerCase());
              console.log('📍 Set pendingDistrictId (normalized):', details.Address_DistrictId.toLowerCase());
            }
            if (details.Address_TownshipId) {
              setPendingTownshipId(details.Address_TownshipId.toLowerCase());
              console.log('📍 Set pendingTownshipId (normalized):', details.Address_TownshipId.toLowerCase());
            }
          }

        } catch (error) {
          console.error('Error loading branch office details:', error);
          // Fallback to basic data from props
          setCode(branchOffice.code);
          setDescription(branchOffice.name);
          setEmail(branchOffice.email || '');
          setIsActive(branchOffice.isActive);
          setAddressDetail(branchOffice.address || '');
          setLocationCode(branchOffice.fiscalCode || '');

          // Don't load coordinates in edit mode - they may not match the address

          setGeolocationData({
            Country: branchOffice.country || 'Panamá',
            CountryId: '',
            Province: branchOffice.province || '',
            ProvinceId: '',
            District: branchOffice.district || '',
            DistrictId: '',
            Township: branchOffice.township || '',
            TownshipId: '',
          } as GeolocationData);
        }
      }
    };

    if (isOpen && isEditMode) {
      loadBranchOfficeDetails();
    }
  }, [isEditMode, branchOffice, selectedCompany?.id, isOpen]);

  // Set pending district ID once districts are loaded
  useEffect(() => {
    if (pendingDistrictId && districts && districts.length > 0) {
      console.log('🔍 Checking district - pendingDistrictId:', pendingDistrictId);
      // IDs are already normalized to lowercase
      const districtExists = districts.some((d: any) => d.DistrictId === pendingDistrictId);
      if (districtExists) {
        console.log('✅ Setting district ID:', pendingDistrictId);
        setSelectedDistrictId(pendingDistrictId);
        setPendingDistrictId(null);
      } else {
        console.log('❌ District ID not found in list:', pendingDistrictId);
        console.log('Available IDs:', districts.map((d: any) => d.DistrictId));
      }
    }
  }, [districts, pendingDistrictId]);

  // Set pending township ID once townships are loaded
  useEffect(() => {
    if (pendingTownshipId && townships && townships.length > 0) {
      console.log('🔍 Checking township - pendingTownshipId:', pendingTownshipId);
      // IDs are already normalized to lowercase
      const townshipExists = townships.some((t: any) => t.TownshipId === pendingTownshipId);
      if (townshipExists) {
        console.log('✅ Setting township ID:', pendingTownshipId);
        setSelectedTownshipId(pendingTownshipId);
        setPendingTownshipId(null);
      } else {
        console.log('❌ Township ID not found in list:', pendingTownshipId);
        console.log('Available IDs:', townships.map((t: any) => t.TownshipId));
      }
    }
  }, [townships, pendingTownshipId]);

  const [coordinateError, setCoordinateError] = useState('');

  const handleLocationSelect = (latitude: number, longitude: number, address?: string) => {
    // Validar coordenadas dentro de Panamá (DGI B304/B304a)
    if (latitude < 7 || latitude > 9.5) {
      setCoordinateError('Latitud fuera del rango de Panamá (7° a 9.5°)');
    } else if (longitude < -83 || longitude > -77) {
      setCoordinateError('Longitud fuera del rango de Panamá (-83° a -77°)');
    } else {
      setCoordinateError('');
    }
    setCoordinates({ lat: latitude, lng: longitude });
    if (address) {
      setAddressDetail(address);
    }
  };

  const handleCodeChange = (value: string) => {
    // Allow only numeric characters and limit to 4 characters
    const numeric = value.replace(/[^0-9]/g, '');
    const truncated = numeric.substring(0, 4);
    setCode(truncated);

    // Validate
    if (truncated.length === 0) {
      setCodeError('El código es requerido');
    } else if (truncated.length < 4) {
      setCodeError('El código debe tener exactamente 4 dígitos numéricos');
    } else {
      setCodeError('');
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);

    // Validate email
    if (value && value.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setEmailError('Email inválido');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  };

  const handleFetchGeolocation = async () => {
    if (!coordinates) return;

    try {
      const data = await geolocationMutation.mutateAsync({
        latitude: coordinates.lat.toString(),
        longitude: coordinates.lng.toString(),
      });

      console.log('=== Geolocation Data Received ===');
      console.log('Full data:', data);

      setGeolocationData(data);

      // Clear pending IDs so they don't overwrite the new geolocation data
      setPendingDistrictId(null);
      setPendingTownshipId(null);

      // Pre-populate the form with geolocation data IDs
      if (data.ProvinceId) setSelectedProvinceId(data.ProvinceId);
      if (data.DistrictId) setSelectedDistrictId(data.DistrictId);
      if (data.TownshipId) setSelectedTownshipId(data.TownshipId);

      // Obtener código de ubicación fiscal
      if (data.Province && data.District && data.Township) {
        try {
          console.log('📍 Obteniendo código de ubicación fiscal...');
          console.log('Parámetros enviados a la API:', {
            province: data.Province,
            district: data.District,
            township: data.Township,
          });

          const fiscalCode = await branchOfficeService.getFiscalCode(
            data.Province,
            data.District,
            data.Township
          );

          console.log('✅ Respuesta completa de fiscal code:', fiscalCode);
          console.log('✅ Tipo de respuesta:', typeof fiscalCode);
          setLocationCode(fiscalCode);
        } catch (error: any) {
          console.error('❌ Error obteniendo código de ubicación:', error);
          console.error('❌ Detalles del error:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
          });
          Swal.fire({
            title: 'Error',
            text: 'No se pudo obtener el código de ubicación. Por favor, inténtelo nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
          });
        }
      }

      setStep(2);
    } catch (error) {
      console.error('Error fetching geolocation:', error);
    }
  };

  const handleSubmit = async () => {
    console.log(`=== Branch Office ${isEditMode ? 'Update' : 'Create'} ===`);
    console.log('Company ID:', selectedCompany?.id);
    console.log('Is Edit Mode:', isEditMode);
    console.log('Branch Office ID:', branchOffice?.id);

    // Validate email format
    if (emailError) {
      Swal.fire({
        title: 'Errores de validación',
        text: 'Por favor corrija los errores en el formulario antes de continuar',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!selectedCompany?.id) {
      console.error('Missing company ID');
      Swal.fire({
        title: 'Error',
        text: 'No se pudo obtener el ID de la compañía',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    // Plan validation for branch offices
    if (!isEditMode) {
      const validation = validateCanAddBranchOffice();

      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de sucursales alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
          // Navigate to plans page
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
    }

    // For edit mode, update branch office and address if needed
    if (isEditMode && branchOffice) {
      try {
        // Step 1: Update address if we have an AddressId and location data
        if (currentAddressId && selectedProvinceId && selectedDistrictId && selectedTownshipId) {
          const addressPayload: any = {
            AddressId: currentAddressId,
            AddressDetail: addressDetail,
            CountryId: geolocationData?.CountryId || 'PA',
            ProvinceId: selectedProvinceId,
            DistrictId: selectedDistrictId,
            TownshipId: selectedTownshipId,
            AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B',
            AddressFiscalCode: locationCode,
          };

          // Solo incluir coordenadas si el usuario seleccionó una nueva ubicación
          if (coordinates) {
            addressPayload.Longitude = coordinates.lng.toString();
            addressPayload.Latitude = coordinates.lat.toString();
          }

          console.log('📍 Updating address with payload:', addressPayload);
          await branchOfficeService.updateAddress(addressPayload);
          console.log('✅ Address updated successfully');
        }

        // Step 2: Update branch office
        const updatePayload = {
          BranchOfficeId: branchOffice.id,
          CompanyId: selectedCompany.id,
          CountryId: null,
          Code: code,
          Description: description,
          IsActive: isActive,
          EmailBranchOffice: email, // Aludra expects EmailBranchOffice
          AddressId: currentAddressId || undefined,
          AddressFiscalCode: locationCode || branchOffice.fiscalCode,
        };

        console.log('🏢 Update branch office payload:', updatePayload);

        await updateBranchOfficeMutation.mutateAsync({
          id: branchOffice.id,
          data: updatePayload as any,
        });

        Swal.fire({
          title: 'Éxito',
          text: 'Sucursal actualizada exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
        handleClose();
        // Don't call onBranchOfficeCreated in edit mode since it expects a different type
        return;
      } catch (error: any) {
        console.error('Error updating branch office:', error);
        const errorMessage = getFriendlyErrorMessage(error);
        Swal.fire({
          title: 'Error al actualizar la sucursal',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
        return;
      }
    }

    // For create mode, validate all required fields
    if (!coordinates) {
      console.error('Missing coordinates');
      Swal.fire({
        title: 'Error',
        text: 'No se encontraron las coordenadas. Por favor seleccione una ubicación en el mapa.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!geolocationData) {
      console.error('Missing geolocation data');
      Swal.fire({
        title: 'Error',
        text: 'No se encontraron los datos de geolocalización.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    if (!locationCode) {
      console.error('Missing location code');
      Swal.fire({
        title: 'Error',
        text: 'No se pudo obtener el código de ubicación fiscal.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    try {
      // Step 1: Create address with the correct AddressTypeId GUID and AddressFiscalCode
      console.log('Creating address...');
      console.log('Address payload:', {
        AddressDetail: addressDetail,
        CountryId: geolocationData.CountryId,
        ProvinceId: selectedProvinceId,
        DistrictId: selectedDistrictId,
        TownshipId: selectedTownshipId,
        Longitude: coordinates.lng.toString(),
        Latitude: coordinates.lat.toString(),
        AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B', // GUID correcto (hardcoded)
        AddressFiscalCode: locationCode, // Código de ubicación fiscal obtenido previamente
      });

      const addressResponse = await createAddressMutation.mutateAsync({
        AddressDetail: addressDetail,
        CountryId: geolocationData.CountryId,
        ProvinceId: selectedProvinceId,
        DistrictId: selectedDistrictId,
        TownshipId: selectedTownshipId,
        Longitude: coordinates.lng.toString(),
        Latitude: coordinates.lat.toString(),
        AddressTypeId: '42ACBCF4-BE83-4F03-8B5E-5760FC92B59B', // GUID correcto (hardcoded)
        AddressFiscalCode: locationCode, // Código de ubicación fiscal obtenido previamente
      });

      console.log('Address created successfully!');
      console.log('Full address response:', JSON.stringify(addressResponse, null, 2));
      console.log('AddressId extracted:', addressResponse.AddressId);
      console.log('AddressFiscalCode in response:', addressResponse.AddressFiscalCode);

      // Validate that we got an AddressId
      if (!addressResponse.AddressId) {
        console.error('ERROR: No AddressId received from address creation!');
        console.error('Response keys:', Object.keys(addressResponse));
        throw new Error('No se recibió AddressId de la creación de dirección');
      }

      console.log('===========================================');
      console.log('🚀 CREATING BRANCH OFFICE - PAYLOAD DETAILS');
      console.log('===========================================');

      const branchOfficePayload = {
        CompanyId: selectedCompany.id,
        Code: code,
        Description: description,
        IsActive: isActive,
        EmailBranchOffice: email, // Aludra expects EmailBranchOffice
        AddressId: addressResponse.AddressId,
        addressFiscalCode: locationCode, // Código de ubicación fiscal
      };

      console.log('Full payload object:', branchOfficePayload);
      console.log('Payload as JSON string:', JSON.stringify(branchOfficePayload, null, 2));
      console.log('Individual field values:');
      console.log('  - CompanyId:', selectedCompany.id, '(type:', typeof selectedCompany.id, ')');
      console.log('  - Code:', code, '(type:', typeof code, ', length:', code?.length, ')');
      console.log('  - Description:', description, '(type:', typeof description, ', length:', description?.length, ')');
      console.log('  - IsActive:', isActive, '(type:', typeof isActive, ')');
      console.log('  - EmailBranchOffice:', email, '(type:', typeof email, ', length:', email?.length, ')');
      console.log('  - AddressId:', addressResponse.AddressId, '(type:', typeof addressResponse.AddressId, ')');
      console.log('  - addressFiscalCode:', locationCode, '(type:', typeof locationCode, ')');
      console.log('===========================================');

      // Step 3: Create branch office
      const branchOffice = await createBranchOfficeMutation.mutateAsync(branchOfficePayload);

      console.log('Branch office created:', branchOffice);

      if (onBranchOfficeCreated) {
        onBranchOfficeCreated(branchOffice);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error creating branch office:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });
      // Sin diálogo acá a propósito: el `onError` de `useCreateBranchOffice` ya
      // abre el modal de error del portal, con este mismo mensaje amigable
      // (la traducción se movió al hook). Antes salían los dos apilados.
    }
  };

  const handleClose = () => {
    // Reset form
    setStep(1);
    setCoordinates(null);
    setGeolocationData(null);
    setSelectedProvinceId('');
    setSelectedDistrictId('');
    setSelectedTownshipId('');
    setPendingDistrictId(null);
    setPendingTownshipId(null);
    setCurrentAddressId(null);
    setAddressDetail('');
    setLocationCode('');
    setCode('');
    setCodeError('');
    setDescription('');
    setEmail('');
    setEmailError('');
    setIsActive(true);
    onClose();
  };

  // In edit mode, allow skipping map selection (coordinates are optional)
  const isStep1Valid = isEditMode || coordinates !== null;
  const isStep2Valid =
    geolocationData?.CountryId &&
    selectedProvinceId &&
    selectedDistrictId &&
    selectedTownshipId &&
    addressDetail &&
    locationCode &&
    code &&
    code.length === 4 &&
    !codeError &&
    description;

  // Debug validation and dropdown data
  console.log('=== Step 2 Debug ===');
  console.log('Validation:', {
    isStep2Valid,
    country: geolocationData?.Country,
    countryId: geolocationData?.CountryId,
    selectedProvinceId: selectedProvinceId,
    selectedDistrictId: selectedDistrictId,
    selectedTownshipId: selectedTownshipId,
    addressDetail: addressDetail,
    locationCode: locationCode,
    code: code,
    description: description,
  });
  console.log('Dropdown data:', {
    provinces: provinces?.length,
    districts: districts?.length,
    townships: townships?.length,
  });
  if (provinces && provinces.length > 0) {
    console.log('Sample province:', provinces[0]);
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={MODAL_BACKDROP} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn('flex max-h-[calc(100vh-2rem)] max-w-4xl flex-col', MODAL_PANEL)}
              >
                {/* ---------- HEAD ---------- */}
                <div className={cn(MODAL_HEAD, 'shrink-0')}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3.5">
                      <span className={MODAL_ICON_TILE}>
                        <BuildingOffice2Icon />
                      </span>
                      <div className="min-w-0">
                        <div className={MODAL_KICKER}>
                          {isEditMode && code ? `Sucursal · ${code}` : 'Administración · Sucursal'}
                        </div>
                        <Dialog.Title as="h3" className={cn(MODAL_TITLE, 'mt-1')}>
                          {isEditMode ? 'Editar Sucursal' : 'Crear Nueva Sucursal'}
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      aria-label="Cerrar"
                      data-testid="branch-office-form-modal-close-button"
                      className={MODAL_CLOSE_X}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Progress indicator */}
                  <div className="mt-[22px]">
                    <ModalStepper steps={['Ubicación', 'Detalles']} current={step - 1} />
                  </div>
                </div>

                {/* ---------- BODY ---------- */}
                <div className={cn(MODAL_BODY, 'min-h-0 flex-1', FORM_SKIN)}>
                  {/* Step 1: Map Selection */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div className="inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.24em] text-teal-500">
                        <span aria-hidden="true" className="h-px w-[22px] bg-teal-500" />
                        Paso 1 · Ubicación geográfica
                      </div>
                      <div>
                        <h3 className="text-lg font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                          ¿Dónde está ubicada la sucursal?
                        </h3>
                        <p className="mt-1.5 text-[14px] text-secondary-500 dark:text-secondary-400">
                          Seleccione la ubicación de la sucursal en el mapa haciendo clic en el punto deseado.
                        </p>
                      </div>
                      <div className="overflow-hidden rounded-[14px] border border-[#ececec] dark:border-secondary-700">
                        <LocationPicker
                          onLocationSelect={handleLocationSelect}
                          height="500px"
                          showSearch={false}
                        />
                      </div>
                      {coordinates && (
                        <div
                          data-testid="branch-office-form-modal-coordinates-text"
                          className="flex items-center gap-2 rounded-[10px] border border-teal-500/20 bg-teal-500/[.06] px-3.5 py-2.5 text-[13px] text-secondary-600 dark:text-secondary-300">
                          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                          Coordenadas seleccionadas:{' '}
                          <b className="font-mono font-bold text-ink-850 dark:text-white">
                            {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
                          </b>
                        </div>
                      )}
                      {coordinateError && (
                        <p className="text-[13px] text-danger-600 dark:text-danger-400" data-testid="branch-office-form-modal-coordinate-error">{coordinateError}</p>
                      )}
                    </div>
                  )}

                  {/* Step 2: Address and Branch Office Details */}
                  {step === 2 && (
                    <div className="space-y-[18px]">
                      <div>
                        <div className="inline-flex items-center gap-2.5 text-[12px] font-bold uppercase tracking-[0.24em] text-teal-500">
                          <span aria-hidden="true" className="h-px w-[22px] bg-teal-500" />
                          Paso 2 · Ubicación geográfica
                        </div>
                        <h3 className="mt-2.5 text-lg font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                          ¿Dónde está ubicada la sucursal?
                        </h3>
                        <p className="mt-1.5 text-[14px] text-secondary-500 dark:text-secondary-400">
                          Al seleccionar la ubicación, el código fiscal se genera automáticamente.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Country - Read only from geolocation */}
                        <Input
                          label="País"
                          value={geolocationData?.Country || ''}
                          disabled
                          required
                         data-testid="branch-office-form-modal-country-input"/>

                        {/* Province */}
                        <Select
                          label="Provincia"
                          value={selectedProvinceId}
                          onChange={(e) => {
                            setSelectedProvinceId(e.target.value);
                            setSelectedDistrictId('');
                            setSelectedTownshipId('');
                          }}
                          disabled={isLoadingProvinces}
                          required data-testid="branch-office-form-modal-province-select"
                        >
                          <option value="">Seleccione una provincia</option>
                          {provinces?.map((province: any) => (
                            <option key={province.ProvinceId} value={province.ProvinceId}>
                              {province.Name}
                            </option>
                          ))}
                        </Select>

                        {/* District */}
                        <Select
                          label="Distrito"
                          value={selectedDistrictId}
                          onChange={(e) => {
                            setSelectedDistrictId(e.target.value);
                            setSelectedTownshipId('');
                          }}
                          disabled={!selectedProvinceId || isLoadingDistricts}
                          required data-testid="branch-office-form-modal-district-select"
                        >
                          <option value="">Seleccione un distrito</option>
                          {districts?.map((district: any) => (
                            <option key={district.DistrictId} value={district.DistrictId}>
                              {district.Name}
                            </option>
                          ))}
                        </Select>

                        {/* Township (Corregimiento) */}
                        <Select
                          label="Corregimiento"
                          value={selectedTownshipId}
                          onChange={async (e) => {
                            const newTownshipId = e.target.value;
                            setSelectedTownshipId(newTownshipId);

                            // Recalculate fiscal code when township changes
                            if (newTownshipId && selectedProvinceId && selectedDistrictId) {
                              const selectedProvince = provinces?.find((p: any) => p.ProvinceId === selectedProvinceId);
                              const selectedDistrict = districts?.find((d: any) => d.DistrictId === selectedDistrictId);
                              const selectedTownship = townships?.find((t: any) => t.TownshipId === newTownshipId);

                              if (selectedProvince && selectedDistrict && selectedTownship) {
                                try {
                                  console.log('🔄 Recalculando código fiscal para:', {
                                    province: selectedProvince.Name,
                                    district: selectedDistrict.Name,
                                    township: selectedTownship.Name,
                                  });
                                  const fiscalCode = await branchOfficeService.getFiscalCode(
                                    selectedProvince.Name,
                                    selectedDistrict.Name,
                                    selectedTownship.Name
                                  );
                                  console.log('✅ Nuevo código fiscal:', fiscalCode);
                                  setLocationCode(fiscalCode);
                                } catch (error) {
                                  console.error('❌ Error obteniendo código fiscal:', error);
                                  Swal.fire({
                                    title: 'Error',
                                    text: 'No se pudo obtener el código de ubicación fiscal.',
                                    icon: 'error',
                                    confirmButtonText: 'Aceptar',
                                  });
                                }
                              }
                            }
                          }}
                          disabled={!selectedDistrictId || isLoadingTownships}
                          required data-testid="branch-office-form-modal-township-select"
                        >
                          <option value="">Seleccione un corregimiento</option>
                          {townships?.map((township: any) => (
                            <option key={township.TownshipId} value={township.TownshipId}>
                              {township.Name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      {/* Address Detail */}
                      <Input
                        label="Detalle de Dirección"
                        value={addressDetail}
                        onChange={(e) => setAddressDetail(e.target.value)}
                        placeholder="Ej: Edificio X, Piso 5, Oficina 501"
                        required
                       data-testid="branch-office-form-modal-address-detail-input"/>

                      {/*
                        Codigo fiscal: el diseño lo saca del input gris y lo pone
                        como dato destacado del bloque. Sigue siendo de solo
                        lectura y sigue viniendo del mismo estado (`locationCode`).
                      */}
                      <div className={cn(MODAL_CALLOUT, 'p-6')}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[#0d9488]">
                              <ShieldCheckIcon className="h-3 w-3" />
                              Código de Ubicación Fiscal <span className="text-[#b8503a]">*</span>
                            </div>
                            <div
                              data-testid="branch-office-form-modal-fiscal-code-input"
                              className="mt-2.5 font-mono text-[30px] font-extrabold tracking-[0.06em] text-ink-850 dark:text-white">
                              {locationCode || '—'}
                            </div>
                            <div className={cn(FIELD_HINT, 'mt-2')}>
                              Generado automáticamente según la ubicación seleccionada
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            <span className="font-mono text-[11px] tracking-[0.20em] text-secondary-400">
                              FORMATO DGI
                            </span>
                            <span className="font-mono text-[12px] font-bold text-[#6b239e]">
                              PROV · DIST · CORR
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Branch Office Details */}
                      <div className="flex items-baseline gap-3 border-t border-dashed border-[#eef0f2] pt-[22px] dark:border-secondary-700">
                        <span className={SECTION_NUM}>02</span>
                        <h4 className="text-base font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
                          Información de la Sucursal
                        </h4>
                        <span className="h-px flex-1" style={DASHED_RULE_STYLE} aria-hidden="true" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Código"
                          value={code}
                          onChange={(e) => handleCodeChange(e.target.value)}
                          placeholder="Ej: 0001"
                          maxLength={4}
                          error={codeError}
                          helperText="4 dígitos numéricos (0-9)"
                          required
                         data-testid="branch-office-form-modal-code-input"/>

                        <Input
                          label="Descripción"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Ej: Sucursal Principal"
                          required
                         data-testid="branch-office-form-modal-description-input"/>

                        <Input
                          label="Email"
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          placeholder="Ej: sucursal@empresa.com"
                          error={emailError}
                          className="md:col-span-2"
                         data-testid="branch-office-form-modal-email-input"/>
                      </div>

                      {/* Estado de la sucursal como fila de ajuste del diseño */}
                      <label
                        htmlFor="isActive"
                        className="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5 rounded-xl border border-[#eef0f2] bg-[#fbfbfc] px-[18px] py-4 dark:border-secondary-700 dark:bg-secondary-900"
                      >
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="h-[18px] w-[18px] rounded-[6px] border-secondary-300 text-[#0d9488] focus:ring-teal-500/40"
                         data-testid="branch-office-form-modal-active-checkbox"/>
                        <span>
                          <span className="block text-sm font-bold text-ink-850 dark:text-white">
                            Sucursal activa
                          </span>
                          <span className="mt-0.5 block text-xs text-secondary-500 dark:text-secondary-400">
                            Disponible para emitir documentos desde esta sucursal.
                          </span>
                        </span>
                        <span
                          className={cn(
                            'font-mono text-[11px] font-bold uppercase tracking-[0.18em]',
                            isActive ? 'text-[#0d9488]' : 'text-secondary-400'
                          )}
                        >
                          Estado · {isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* ---------- FOOT ---------- */}
                <div className={cn(MODAL_FOOT, 'shrink-0')}>
                  <div>
                    {step === 2 && (
                      <Button variant="outline" onClick={() => setStep(1)} data-testid="branch-office-form-modal-back-button">
                        <ArrowLeftIcon className="h-3.5 w-3.5" />
                        Atrás
                      </Button>
                    )}
                  </div>
                  <div className="ml-auto flex gap-2.5">
                    <Button variant="outline" onClick={handleClose} data-testid="branch-office-form-modal-cancel-button">
                      Cancelar
                    </Button>
                    {step === 1 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          // In edit mode without new coordinates, just go to step 2
                          if (isEditMode && !coordinates) {
                            setStep(2);
                          } else {
                            handleFetchGeolocation();
                          }
                        }}
                        disabled={!isStep1Valid || geolocationMutation.isPending}
                        data-testid="branch-office-form-modal-continue-button"
                      >
                        {geolocationMutation.isPending ? 'Cargando...' : 'Continuar'}
                      </Button>
                    )}
                    {step === 2 && (
                      <Button
                        variant="success"
                        data-testid="branch-office-form-modal-submit-button"
                        onClick={() => {
                          console.log('Button clicked! isStep2Valid:', isStep2Valid);
                          if (!isStep2Valid) {
                            console.error('Form validation failed:', {
                              country: geolocationData?.Country,
                              countryId: geolocationData?.CountryId,
                              selectedProvinceId,
                              selectedDistrictId,
                              selectedTownshipId,
                              addressDetail,
                              code,
                              description,
                            });
                            Swal.fire({
                              title: 'Campos incompletos',
                              text: 'Por favor complete todos los campos requeridos',
                              icon: 'warning',
                              confirmButtonText: 'Aceptar',
                            });
                            return;
                          }
                          handleSubmit();
                        }}
                        disabled={
                          createAddressMutation.isPending ||
                          createBranchOfficeMutation.isPending ||
                          updateBranchOfficeMutation.isPending
                        }
                      >
                        {createAddressMutation.isPending || createBranchOfficeMutation.isPending || updateBranchOfficeMutation.isPending
                          ? (isEditMode ? 'Actualizando...' : 'Creando...')
                          : (
                            <>
                              {isEditMode ? 'Actualizar Sucursal' : 'Crear Sucursal'}
                              <CheckIcon className="h-3.5 w-3.5" />
                            </>
                          )}
                      </Button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
